import { io } from "socket.io-client";
import ENV from "config/environment";

let socket;

export const initiateSocket = () => {
  if (socket?.connected) return;
  if (socket) socket.disconnect();

  const baseUrl = ENV.API_BASE_URL.replace(/\/api$/, "");
  socket = io(baseUrl, {
    withCredentials: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
  });

  // Don't tear down the socket on a transient auth error — calling disconnect()
  // here kills socket.io's automatic reconnection. Let the built-in retry
  // (reconnectionAttempts) handle it so a refreshed token can re-authenticate.
  socket.on("connect_error", (err) => {
    if (err.message === "Authentication error") {
      console.warn("[socket] authentication error; awaiting reconnect/refresh");
    }
  });
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

export const joinConversation = (conversationId) => {
  if (socket) socket.emit("joinConversation", conversationId);
};

export const leaveConversation = (conversationId) => {
  if (socket) socket.emit("leaveConversation", conversationId);
};

export const subscribeToMessages = (cb) => {
  if (!socket) return;
  socket.on("receiveMessage", (msg) => cb(msg));
};

export const subscribeToConversations = (cb) => {
  if (!socket) return;
  socket.on("conversationUpdated", (data) => cb(data));
};

export const unsubscribeFromMessages = () => {
  if (socket) socket.off("receiveMessage");
};

export const unsubscribeFromConversations = () => {
  if (socket) socket.off("conversationUpdated");
};

// Presence — emitted by server on connect/disconnect
export const subscribeToPresence = (cb) => {
  if (!socket) return;
  socket.on("userStatusChanged", (data) => cb(data));
};

export const unsubscribeFromPresence = () => {
  if (socket) socket.off("userStatusChanged");
};

// ── Feed real-time events ─────────────────────────────────────
// feed:new_post  → { postId, authorId, channel, type, visibility, teamId }
export const subscribeToFeedNewPost = (cb) => {
  if (!socket) return;
  socket.off("feed:new_post"); // remove any stale listener before re-adding
  socket.on("feed:new_post", (data) => cb(data));
};
export const unsubscribeFromFeedNewPost = () => {
  if (socket) socket.off("feed:new_post");
};

// feed:reaction_updated → { postId, reactionCounts }
export const subscribeToFeedReaction = (cb) => {
  if (!socket) return;
  socket.off("feed:reaction_updated");
  socket.on("feed:reaction_updated", (data) => cb(data));
};
export const unsubscribeFromFeedReaction = () => {
  if (socket) socket.off("feed:reaction_updated");
};

// feed:comment_added → { postId }
export const subscribeToFeedComment = (cb) => {
  if (!socket) return;
  socket.off("feed:comment_added");
  socket.on("feed:comment_added", (data) => cb(data));
};
export const unsubscribeFromFeedComment = () => {
  if (socket) socket.off("feed:comment_added");
};

// Session events
export const subscribeToSessionEvents = (cb) => {
  if (!socket) return;
  socket.on("sessionBooked", (data) => cb({ type: "booked", ...data }));
  socket.on("sessionConfirmed", (data) => cb({ type: "confirmed", ...data }));
  socket.on("sessionDeclined", (data) => cb({ type: "declined", ...data }));
  socket.on("sessionCancelled", (data) => cb({ type: "cancelled", ...data }));
  socket.on("sessionCompleted", (data) => cb({ type: "completed", ...data }));
};

export const unsubscribeFromSessionEvents = () => {
  if (!socket) return;
  socket.off("sessionBooked");
  socket.off("sessionConfirmed");
  socket.off("sessionDeclined");
  socket.off("sessionCancelled");
  socket.off("sessionCompleted");
};
